import React, { useState } from 'react';
import cx from 'classnames';

import {
  TruncatedText,
  useEditContext,
  usePageContext,
} from '@xingternal/pages-common';

import type { NewsProps } from '../News';
import NewsItemActor from './NewsItemActor';
import NewsItemSocial from '../NewsItemSocial';
import NewsItemAttachment from './NewsItemAttachment';
import NewsItemActions from './NewsItemActions';

import { CONTENT_FEED_TYPE_GENERIC, PUBLISHED } from '../constants';
import { EntityPageFeedCollectionFragment } from '../EntityPageFeedCollectionFragment.gql-types';

import styles from './styles.css';

type NewsItemProps = Pick<
  NewsProps,
  'actor' | 'feedQueryVars' | 'isDetailPage' | 'isFollowingPage'
> & {
  isPaidPage: boolean;
  feedUpdate: EntityPageFeedCollectionFragment;
  onClick?: () => void;
  setLazyLoadingIsAllowed: (isAllowed: boolean) => void;
  selectedPosting: () => void;
  showActor: boolean;
  videoPlaying: React.RefObject<any>;
};

// eslint-disable-next-line complexity
const NewsItem = ({
  actor,
  feedQueryVars,
  isDetailPage = false,
  isFollowingPage = false,
  isPaidPage,
  feedUpdate,
  onClick,
  setLazyLoadingIsAllowed,
  selectedPosting,
  showActor = true,
  videoPlaying,
}: NewsItemProps) => {
  const { pageContext } = usePageContext();
  const { isEditing } = useEditContext();
  const { pageSlug, globalId: pageGlobalId } = pageContext;
  const [isItemLoading, setIsItemLoading] = useState(false);
  const item = feedUpdate.object;
  const pageId = feedQueryVars.id;

  const type = item?.__typename;

  if (type === 'EntityPageContentFeedPlainTextPostingPost') {
    return null;
  }

  const { isPinned, metadata, publishedAt, globalId } = feedUpdate;

  const textTruncationLimit = 500;
  const attachment =
    item?.__typename === 'EntityPageContentFeedPostingPost'
      ? item.attachment
      : {};
  const attachmentType = attachment?.__typename || '';

  const isPinnedOldArticle = type !== CONTENT_FEED_TYPE_GENERIC && isPinned;
  const publicationState =
    item?.__typename === 'EntityPageContentFeedPostingPost'
      ? item.publicationState
      : undefined;
  const createdAt =
    item?.__typename === 'EntityPageContentFeedPostingPost'
      ? item.createdAt
      : undefined;

  const date =
    publicationState === PUBLISHED || publicationState === undefined
      ? publishedAt
      : createdAt;

  const xbmUrl =
    isEditing && pageSlug
      ? `${window.location.origin}/brandmanager/${pageSlug}/news-reach?sc_o=entity_page_news_module_more_statistics`
      : '';

  const xamUrl =
    isEditing &&
    publicationState === PUBLISHED &&
    (attachmentType === 'PostingsImageAttachment' ||
      attachmentType === 'PostingsLinkAttachment')
      ? `${window.location.origin}/xam/aac?entity_urn=${pageGlobalId}&asset_urn=${globalId}&sc_o=entity_page_news_module_promote`
      : '';

  const comment =
    item?.__typename === 'EntityPageContentFeedArticlesArticlePost'
      ? undefined
      : item?.comment;

  return (
    <article className={cx(styles.item, { [styles.loading]: isItemLoading })}>
      <div
        className={cx(styles.actorGroup, {
          [styles.noActorEdit]: isEditing && !showActor,
        })}
      >
        <NewsItemActor
          actionType={type}
          actor={actor}
          date={date}
          isFollowingPage={isFollowingPage}
          isPinned={isPinned}
          pageId={pageId}
          publicationState={publicationState}
          showActor={showActor}
        />
        {isEditing && type && (
          <NewsItemActions
            activityId={globalId}
            editPosting={onClick}
            feedQueryVars={feedQueryVars}
            articleTypename={type}
            isPinned={isPinned || isPinnedOldArticle}
            isPaidPage={isPaidPage}
            pageId={pageId}
            itemId={
              item?.__typename === 'EntityPageContentFeedPostingPost' ||
              item?.__typename === 'EntityPageContentFeedCompanyArticlePost'
                ? item?.id
                : undefined
            }
            publicationState={publicationState}
            setIsItemLoading={setIsItemLoading}
            setLazyLoadingIsAllowed={setLazyLoadingIsAllowed}
            xamUrl={xamUrl}
            selectedPosting={selectedPosting}
          />
        )}
      </div>

      {comment && (
        <section
          className={cx(showActor ? styles.comment : styles.noActorComment)}
        >
          <TruncatedText limit={textTruncationLimit}>{comment}</TruncatedText>
        </section>
      )}

      <NewsItemAttachment
        actorId={globalId}
        isPinned={isPinned}
        item={item}
        videoPlaying={videoPlaying}
      />

      {globalId && metadata && (
        <NewsItemSocial
          basePath={pageContext?.basePath}
          authorUrn={pageGlobalId}
          isDetailPage={isDetailPage}
          metadata={metadata}
          pageSlug={actor.slug}
          activityGlobalId={globalId}
          xbmUrl={xbmUrl}
        />
      )}
    </article>
  );
};

export default NewsItem;
